import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles(theme => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 16px ${theme.palette.primary.main}80 !important` 
      : `0 0 12px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#FFFFFF !important",
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
  },
  dialogActions: {
    padding: theme.spacing(2, 2.5),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
  },
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
    "& .MuiFormHelperText-root.Mui-error": {
      color: "#ff6b6b",
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.7)"
        : "rgba(0,0,0,0.54)",
    },
    "& .MuiInputLabel-root.Mui-error": {
      color: "#ff6b6b",
    },
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
    paddingTop: theme.spacing(2),
    overflow: "visible",
  },
  menuPaper: {
    maxWidth: "none !important",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    width: "100%",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: "green",
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  selectLabel: {
    color: theme.palette.text.primary,
    background: theme.palette.background.paper,
    paddingLeft: 4,
    paddingRight: 4,
  },
}));

const WebhookSchema = Yup.object().shape({
  name: Yup.string().required("Requerido"),
  trigger: Yup.string().required("Requerido"),
  url: Yup.string().url("URL inválida").required("Requerido"),
  method: Yup.string().required("Requerido"),
});

const EventWebhookModal = ({ open, onClose, webhookId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    trigger: "kanban_move",
    url: "",
    method: "POST",
    headers: "{}",
    payload: "{}",
    queueId: "",
    whatsappId: ""
  };

  const [webhook, setWebhook] = useState(initialState);
  const [queues, setQueues] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);

  useEffect(() => {
    const fetchSelects = async () => {
      try {
        const { data: queueData } = await api.get("/queue");
        setQueues(queueData);
        
        const { data: whatsappData } = await api.get("/whatsapp");
        setWhatsapps(whatsappData);
      } catch (err) {
        console.error(err);
      }
    };
    fetchSelects();
  }, []);

  useEffect(() => {
    const fetchWebhook = async () => {
      if (!webhookId) {
        setWebhook(initialState);
        return;
      }
      try {
        const { data } = await api.get(`/event-webhooks/${webhookId}`);
        setWebhook({
          ...data,
          headers: JSON.stringify(data.headers, null, 2),
          payload: JSON.stringify(data.payload, null, 2)
        });
      } catch (err) {
        toastError(err);
      }
    };
    fetchWebhook();
  }, [webhookId, open]);

  const handleClose = () => {
    onClose();
    setWebhook(initialState);
  };

  const handleSaveWebhook = async values => {
    try {
      let parsedHeaders = {};
      let parsedPayload = {};

      try {
        parsedHeaders = JSON.parse(values.headers || "{}");
      } catch (e) {
        toast.error("Formato JSON inválido en Headers");
        return;
      }

      try {
        parsedPayload = JSON.parse(values.payload || "{}");
      } catch (e) {
        toast.error("Formato JSON inválido en Payload");
        return;
      }

      const webhookData = {
        ...values,
        queueId: values.queueId || null,
        whatsappId: values.whatsappId || null,
        headers: parsedHeaders,
        payload: parsedPayload,
      };

      if (webhookId) {
        await api.put(`/event-webhooks/${webhookId}`, webhookData);
      } else {
        await api.post("/event-webhooks", webhookData);
      }
      toast.success(i18n.t("eventWebhooks.modal.saveSuccess"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.dialogHeader}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {webhookId ? i18n.t("eventWebhooks.modal.editTitle") : i18n.t("eventWebhooks.modal.addTitle")}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <Formik
          initialValues={webhook}
          enableReinitialize={true}
          validationSchema={WebhookSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWebhook(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.container}>
                  <Field
                    as={TextField}
                    label={i18n.t("eventWebhooks.modal.name")}
                    autoFocus
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  
                  <Field name="trigger">
                    {({ field }) => (
                      <TextField
                        {...field}
                        select
                        label={i18n.t("eventWebhooks.modal.trigger")}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                          style: { color: "rgba(255,255,255,0.7)" }
                        }}
                        className={classes.textField}
                        SelectProps={{
                          MenuProps: {
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left"
                            },
                            classes: { paper: classes.menuPaper }
                          }
                        }}
                      >
                        <MenuItem value="kanban_move">{i18n.t("eventWebhooks.modal.triggerOptions.kanban_move")}</MenuItem>
                        <MenuItem value="contact_update">{i18n.t("eventWebhooks.modal.triggerOptions.contact_update")}</MenuItem>
                      </TextField>
                    )}
                  </Field>

                  <Field name="queueId">
                    {({ field }) => (
                      <TextField
                        {...field}
                        select
                        label={i18n.t("eventWebhooks.modal.queue")}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputLabelProps={{ shrink: true, style: { color: "rgba(255,255,255,0.7)" } }}
                        className={classes.textField}
                        SelectProps={{ MenuProps: { getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } } }}
                      >
                        <MenuItem value={""}>Cualquiera</MenuItem>
                        {queues.map(queue => (
                          <MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Field>

                  <Field name="whatsappId">
                    {({ field }) => (
                      <TextField
                        {...field}
                        select
                        label={i18n.t("eventWebhooks.modal.connection")}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputLabelProps={{ shrink: true, style: { color: "rgba(255,255,255,0.7)" } }}
                        className={classes.textField}
                        SelectProps={{ MenuProps: { getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } } }}
                      >
                        <MenuItem value={""}>Cualquiera</MenuItem>
                        {whatsapps.map(whatsapp => (
                          <MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Field>

                  <Field
                    as={TextField}
                    label={i18n.t("eventWebhooks.modal.url")}
                    name="url"
                    error={touched.url && Boolean(errors.url)}
                    helperText={touched.url && errors.url}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />

                  <Field name="method">
                    {({ field }) => (
                      <TextField
                        {...field}
                        select
                        label={i18n.t("eventWebhooks.modal.method")}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                          style: { color: "rgba(255,255,255,0.7)" }
                        }}
                        className={classes.textField}
                        SelectProps={{
                          MenuProps: {
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left"
                            },
                            classes: { paper: classes.menuPaper }
                          }
                        }}
                      >
                        <MenuItem value="POST">POST</MenuItem>
                        <MenuItem value="PUT">PUT</MenuItem>
                        <MenuItem value="GET">GET</MenuItem>
                      </TextField>
                    )}
                  </Field>

                  <Field
                    as={TextField}
                    label={i18n.t("eventWebhooks.modal.headers")}
                    name="headers"
                    error={touched.headers && Boolean(errors.headers)}
                    helperText={touched.headers && errors.headers}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    multiline
                    minRows={3}
                  />

                  <Field
                    as={TextField}
                    label={i18n.t("eventWebhooks.modal.payload") + " - Variables: {{ticket.etiquetas}}, {{ticket.kanban_etapa}}, {{contact.number}}"}
                    name="payload"
                    error={touched.payload && Boolean(errors.payload)}
                    helperText={touched.payload && errors.payload}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    multiline
                    minRows={5}
                  />
                </div>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className={classes.secondaryButton}
                >
                  {i18n.t("queueModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={classes.applyButton}
                >
                  {webhookId
                    ? `${i18n.t("queueModal.buttons.okEdit")}`
                    : `${i18n.t("queueModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={20}
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default EventWebhookModal;
